import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, ArrowUpRight, Mail, Phone, Users } from "lucide-react";

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", primary_contact_name: "", primary_contact_email: "",
    primary_contact_phone: "", notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (e) { toast.error("Could not load clients"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Client name required"); return; }
    try {
      const { data } = await api.post("/clients", form);
      toast.success("Client added");
      setOpen(false);
      setForm({ name: "", company: "", primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "", notes: "" });
      navigate(`/clients/${data.id}`);
    } catch (e) { toast.error("Could not create client"); }
  };

  return (
    <Shell>
      <div className="p-6 md:p-10 max-w-6xl">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-6">
          <div>
            <div className="eyebrow mb-3">Book of clients</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-app tracking-tight leading-none">
              Every client, one roster
            </h1>
            <p className="mt-3 text-muted font-light">
              {clients.length} {clients.length === 1 ? "client" : "clients"} on the books.
            </p>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-client-btn"
                className="rounded-sm bg-app hover:bg-[#000000] text-white h-11 px-6"
              >
                <Plus size={16} strokeWidth={1.5} className="mr-2" /> New client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bevel-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-app">New client</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <F label="Client name">
                  <Input data-testid="client-name-input" placeholder="e.g. The Miller Family · ACME Co."
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </F>
                <F label="Company (optional)">
                  <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                </F>
                <F label="Primary contact — name">
                  <Input value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} />
                </F>
                <F label="Primary contact — email (grants client portal access)">
                  <Input data-testid="client-email-input" type="email" placeholder="client@example.com"
                    value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} />
                </F>
                <F label="Primary contact — phone">
                  <Input value={form.primary_contact_phone} onChange={(e) => setForm({ ...form, primary_contact_phone: e.target.value })} />
                </F>
                <F label="Notes">
                  <Textarea rows={2} className="rounded-sm"
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </F>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="client-create-submit" onClick={create} className="rounded-sm bg-app hover:bg-[#000000]">
                  Create client
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-muted">Loading…</div>
        ) : clients.length === 0 ? (
          <div className="bevel border-hair rounded-sm p-12 text-center" data-testid="clients-empty">
            <div className="font-serif text-3xl text-app mb-2">No clients yet.</div>
            <p className="text-muted font-light max-w-md mx-auto mb-6">
              Add your first client to organize their events in one place. Give them a portal login by adding their email.
            </p>
            <Button onClick={() => setOpen(true)} className="rounded-sm bg-app hover:bg-[#000000]">
              <Plus size={16} strokeWidth={1.5} className="mr-2" /> Add your first client
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients.map(c => (
              <Link
                key={c.id}
                to={`/clients/${c.id}`}
                data-testid={`client-card-${c.id}`}
                className="group block bevel border-hair rounded-sm p-6 card-lift"
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    {c.company && <div className="eyebrow mb-1">{c.company}</div>}
                    <h3 className="font-serif text-2xl text-app truncate">{c.name}</h3>
                  </div>
                  <ArrowUpRight size={18} strokeWidth={1.5} className="text-muted group-hover:text-app transition-colors flex-shrink-0" />
                </div>

                <div className="space-y-1.5 text-sm text-muted font-light">
                  {c.primary_contact_email && (
                    <div className="flex items-center gap-2 truncate">
                      <Mail size={13} strokeWidth={1.5} /><span className="truncate">{c.primary_contact_email}</span>
                    </div>
                  )}
                  {c.primary_contact_phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={13} strokeWidth={1.5} /><span>{c.primary_contact_phone}</span>
                    </div>
                  )}
                </div>

                <div className="mt-5 pt-4 border-t border-hair flex items-center justify-between">
                  <div className="flex items-center gap-2 eyebrow">
                    <Users size={12} strokeWidth={1.5} />
                    <span>{c.event_count} {c.event_count === 1 ? "event" : "events"}</span>
                  </div>
                  {c.next_event && (
                    <div className="text-xs text-app font-light truncate max-w-[60%]">
                      Next: {c.next_event.title}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}

function F({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}
